import React, { useRef, useEffect, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { isEqual } from 'lodash';
import { createStore } from './store/store';
import { createI18nInstance } from './components/handlers/LanguageHandler';
// import useDeepCompareEffect from 'use-deep-compare-effect';

// Instance counter for generating unique IDs
let instanceCounter = 0;

// Per-instance config storage (avoids global document pollution)
const instanceConfigs = new Map();

export function init(appConfig, muiTheme, fcConfig, iFrameDocument) {
  let rootEl = iFrameDocument?.getElementById(
    appConfig?.rootElId ?? 'bookingjs',
  );

  if (!rootEl) {
    rootEl = document.getElementById(appConfig?.rootElId ?? 'bookingjs');
  }

  // Each init() call gets its own Redux store and i18n instance
  // so multiple widgets on the same page don't share state
  const instanceId = `bookingjs_${instanceCounter++}`;
  const store = createStore(instanceId);
  const i18nInstance = createI18nInstance();

  const root = createRoot(rootEl);
  root.render(
    <React.StrictMode>
      <App
        appConfig={appConfig}
        muiTheme={muiTheme}
        fcConfig={fcConfig}
        store={store}
        i18nInstance={i18nInstance}
      />
    </React.StrictMode>,
  );
}

/**
 * Initialises timum booking. For usage in script tags.
 * For usage in React use the 'TimumBooking' component instead.
 *
 * Wraps then app in an iFrame. Useful if more than one bookingJs must be displayed on the same page
 * Or where we encounter css interference.
 * @param {*} appConfig - the config object. See examples/fullExample.htm for all available conf options.
 * @param {*} muiTheme  - a mui theme object. See examples/fullExample.htm for a non exhaustive usage example. Works only for professional plans.
 * @param {*} fcConfig  - configurations for a fullCalendar frontend (set appConfig.calendarFrontend = 'fullCalendar') for this to have any effect.
 *                       Not muiTheme compatible. See https://fullcalendar.io/docs#toc fo all config options
 */
export function initialise(appConfig, muiTheme, fcConfig) {
  const rootEl = document.getElementById(appConfig?.rootElId ?? 'bookingjs');

  const iFrame = document.createElement('iframe');
  iFrame.style = 'border-width:0';
  iFrame.height = appConfig.height;
  iFrame.width = '100%';
  rootEl.appendChild(iFrame);

  appConfig.queryString = window.location.search;
  appConfig.queryHash = window.location.hash;

  document.appConfig = appConfig;
  document.muiTheme = muiTheme;
  document.fcConfig = fcConfig;

  const cdnUrl =
    appConfig.cdnUrl ?? 'https://cdn.timum.de/bookingjs/1/booking.js';

  iFrame.srcdoc = `
  <div id='${
    appConfig?.rootElId ?? 'bookingjs'
  }' style='margin: 0px 16px 0px 8px'>
    <script type='module'>
      import { init } from '${cdnUrl}';
      const context = window.parent.document;
      init(context.appConfig, context.muiTheme, context.fcConfig, window.document);
    </script>
  </div>
  `;
}

/**
 * React component.
 * For usage in script tags use the 'init' function instead.
 * @param {*} appConfig - the config object. See examples/fullExample.htm for all available conf options.
 * @param {*} muiTheme  - a mui theme object. See examples/fullExample.htm for a non exhaustive usage example. Works only for professional plans.
 * @param {*} fcConfig  - configurations for a fullCalendar frontend (set appConfig.calendarFrontend = 'fullCalendar') for this to have any effect.
 *                       Not muiTheme compatible. See https://fullcalendar.io/docs#toc fo all config options
 */
export function TimumBooking({ appConfig, muiTheme, fcConfig, iFrameStyle }) {
  // Generate a stable unique instance ID for this component
  const instanceIdRef = useRef(null);
  if (instanceIdRef.current === null) {
    instanceIdRef.current = `timum_booking_iframe_${instanceCounter++}`;
  }
  const instanceId = instanceIdRef.current;

  // const [contentRef, setContentRef] = React.useState(null);
  // const mountNode = contentRef?.contentWindow?.document?.body;
  // const cache = createCache({
  //   key: 'css',
  //   container: contentRef?.contentWindow?.document?.head,
  //   prepend: true,
  // });

  // This implementation is just soo ugly. We always need to redownload bookingjs from our cdn :/
  // unfortunately mui adds its styles to the header of the main page and not of the iFrame
  // tried using emotion's createCache and CacheProvider combo but it had no effect.

  // also tried the solution described here but without success: https://github.com/mui/material-ui/issues/36558#issuecomment-1474984932
  const cdnUrl =
    appConfig.cdnUrl ?? 'https://cdn.timum.de/bookingjs/1/booking.js';

  // Prepare config with query params (don't mutate original prop)
  // Memoized to avoid triggering useEffect on every render
  const processedAppConfig = useMemo(
    () => ({
      ...appConfig,
      queryString: window.location.search,
      queryHash: window.location.hash,
      isComponent: true,
    }),
    [appConfig],
  );

  // Handle prop changes: send postMessage to THIS instance's iframe only
  useEffect(() => {
    const storedConfig = instanceConfigs.get(instanceId);

    if (
      storedConfig &&
      (!isEqual(storedConfig.appConfig, processedAppConfig) ||
        !isEqual(storedConfig.muiTheme, muiTheme) ||
        !isEqual(storedConfig.fcConfig, fcConfig))
    ) {
      const frame = document.getElementById(instanceId);
      if (frame) {
        frame.contentWindow.postMessage({
          type: 'rerender',
          appConfig: processedAppConfig,
          muiTheme,
          fcConfig,
        });
      }
    }

    // Store config for THIS instance
    instanceConfigs.set(instanceId, {
      appConfig: processedAppConfig,
      muiTheme,
      fcConfig,
    });

    // Cleanup on unmount
    return () => {
      instanceConfigs.delete(instanceId);
    };
  }, [instanceId, processedAppConfig, muiTheme, fcConfig]);

  // Also maintain document-level config for backwards compatibility with init()
  // (This is used by the srcdoc script that reads from window.parent.document)
  // Each instance stores under its own key
  useEffect(() => {
    document[`${instanceId}_appConfig`] = processedAppConfig;
    document[`${instanceId}_muiTheme`] = muiTheme;
    document[`${instanceId}_fcConfig`] = fcConfig;

    return () => {
      delete document[`${instanceId}_appConfig`];
      delete document[`${instanceId}_muiTheme`];
      delete document[`${instanceId}_fcConfig`];
    };
  }, [instanceId, processedAppConfig, muiTheme, fcConfig]);

  // this never rerenders on a prop change, because none of the iframes props change.
  // But now with useEffect above, we properly send postMessage to the correct iframe.
  return (
    <iframe
      /* ref={setContentRef} */
      id={instanceId}
      height={appConfig.height ?? '100%'}
      width={'100%'}
      style={{ borderWidth: 0, ...iFrameStyle }}
      srcDoc={`
        <div id='${
          appConfig?.rootElId ?? 'bookingjs'
        }' style='margin: 0px 16px 0px 8px'>
          <script type='module'>
            import { init } from '${cdnUrl}';
            const context = window.parent.document;
            const instanceId = '${instanceId}';
            init(
              context[instanceId + '_appConfig'],
              context[instanceId + '_muiTheme'],
              context[instanceId + '_fcConfig'],
              window.document
            );
          </script>
        </div>
  `}
    ></iframe>
  );
}

/** re-export yup so that users may write validation rules for custom fields */
export * as yup from 'yup';

/** re-export i18next instance so users can opt into localisation */
export * as i18next from 'i18next';

export { default as React } from 'react';
